/**
 * Legend width computation for TimelineColumnLegend legends.
 *
 * ## Layout
 *
 * Calendar legends show hour marks with optional secondary timezone
 * CalendarLegend elements. Width depends entirely on the number of
 * secondary timezone child rows — no text measurement needed.
 *
 *   0 tz children: primary CalendarLegend only (80px base)
 *   1 tz child:    primary compact (36px) + secondary (76px)
 *   2 tz children: primary compact + secondary + secondary compact (36px)
 *
 * ## Constants (from sentinel bounding boxes)
 *
 *   primaryBase    = 80px  (CalendarLegend default width)
 *   primaryCompact = 36px  (CalendarLegend when secondary exists)
 *   secondary      = 76px  (secondary CalendarLegend width)
 *   secondaryExtra = 36px  (each additional secondary after the first)
 *   CALENDAR_OVERHEAD = 60px  (icon + SheetLegend padding + hour marks)
 *
 * ## Verified from
 *
 *   sentinel (debug toast on :calendar):
 *     0 tz children → sentinel ≈ 140, formula = 80 + 60 = 140 ✓
 *     2 tz children → sentinel = 208, formula = 36 + 76 + 36 + 60 = 208 ✓
 */

export interface CalendarLegendLayoutInput {
  childTimezoneCount: number;
}

const CALENDAR_OVERHEAD = 60;

/**
 * Compute the natural width of a TimelineColumnLegend in pixels.
 * Constant-time, no DOM access, no text measurement.
 */
function calendarLegendWidth(
  input: CalendarLegendLayoutInput,
): number {
  if (input.childTimezoneCount === 0) return 80 + CALENDAR_OVERHEAD;
  return 36 + 76 + Math.max(0, input.childTimezoneCount - 1) * 36 + CALENDAR_OVERHEAD;
}

export namespace CalendarLegendLayout {
  export const width = calendarLegendWidth;
}
